import type { GetContractsResponse, GetDPSDetailResponse, GetPPDetailResponse } from "@repo/api";
import type { UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { isAgeInRange } from "../calculations/dateUtils";
import { findPensionNovelContract } from "../calculations/pension";

const MAX_TAX_SAVING_CONTRIBUTION = 5700;
const NOVEL_MIN_AGE = 18;
const NOVEL_MAX_AGE = 70;

export function usePensionNovelBanner(
    contractsQuery: UseQueryResult<GetContractsResponse[], unknown>,
    pensionDetailQuery: UseQueryResult<GetDPSDetailResponse | GetPPDetailResponse, unknown>,
) {
    const { data, isSuccess: contractsSuccess } = contractsQuery;

    const contract = useMemo(() => {
        if (!data) return undefined;
        const contract = findPensionNovelContract(data);
        if (!contract) return undefined;
        // Only show if monthly contribution (amount) is below max
        const monthlyContribution = pensionDetailQuery.data?.contract.monthlyContributionClient ?? 0;
        if (monthlyContribution >= MAX_TAX_SAVING_CONTRIBUTION) return undefined;

        const birthDate = pensionDetailQuery.data?.clientInfo?.dateOfBirth;
        if (!isAgeInRange(birthDate, NOVEL_MIN_AGE, NOVEL_MAX_AGE)) return undefined;

        return contract;
    }, [data, pensionDetailQuery.data]);

    // A disabled query (no matching UF contract → enabled: false) will never fetch,
    // so fetchStatus stays "idle". Only count it as loading when it's actually fetching.
    const detailLoading = pensionDetailQuery.fetchStatus === "fetching";

    return {
        contract,
        isLoading: !contractsSuccess || detailLoading,
        monthlyContribution: pensionDetailQuery.data?.contract.monthlyContributionClient ?? 0,
    };
}
